<template>
  <b-modal
    :modal-class="[
      'document-modal',
      'view-single-document-modal',
      isArray ? 'multiple-documents' : ''
    ]"
    v-model="editorShown"
    :size="modalSize"
    no-close-on-backdrop
    hide-header
    hide-footer
    centered
  >
    <div class="d-flex h-100">
      <div v-if="isArray" class="sidebar-forms-container bg-extra-light-gray">
        <template v-if="documents.length">
          <div
            class="d-flex font-weight-bold px-3 my-3"
            style="font-size:16px;"
          >
            Documents
            <div v-if="groupHtml" class="ml-auto">
              <b-dropdown variant="info" right toggle-class="" size="sm">
                <template #button-content>
                  <i class="fa fa-cog mr-2" aria-hidden="true"></i>
                </template>
                <b-dropdown-item
                  @click="() => showReorderModal(sendFax, 'Fax All')"
                >
                  Fax All
                </b-dropdown-item>
                <b-dropdown-item
                  @click="() => showReorderModal(savePdfGroup, 'Download All')"
                  :disabled="downloadingPDF"
                >
                  Download All
                </b-dropdown-item>
                <b-dropdown-item
                  @click="() => showReorderModal(printGroup, 'Print All')"
                >
                  Print All
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
          <div class="sidebar-forms">
            <div
              v-for="document in documents"
              :key="document.documentId"
              :id="`document-${document.documentId}`"
              :class="document == currentDocument ? 'active' : ''"
              class="sidebar-menu-form-item d-flex align-items-center"
              @click="setCurrentDocument(document)"
            >
              <div class="w-100 justify-content-end pt-2 pb-2">
                <b-container class="ml-0 p-0">
                  <b-row>
                    <b-col cols="5"
                      ><b-badge
                        :variant="getStatusClass(document)"
                        class="text-white"
                        >{{ getStatus(document).toUpperCase() }}
                      </b-badge></b-col
                    >
                    <b-col cols="6" style="margin-left: auto"
                      ><div>Ref#: {{ document.documentRefNumber }}</div></b-col
                    >
                  </b-row>
                </b-container>
                <div style="font-size: 0.925em">
                  {{ document.form.name }}
                </div>
                <div style="font-size: 0.925em">
                  {{ formatDate(document.effectiveDate) }}
                </div>
                <div style="font-size: 0.875em">
                  {{ formatEocObj(document.episodeOfCare) }}
                </div>
                <div
                  v-if="
                    document.totalSignatures &&
                      document.totalSignatures - document.completedSignatures !=
                        0
                  "
                  class="d-inline"
                  style="font-size: 0.775em; color: #d9534f"
                >
                  {{ document.totalSignatures - document.completedSignatures }}
                  <p class="d-inline">
                    {{
                      document.totalSignatures - document.completedSignatures !=
                      1
                        ? "pending signatures"
                        : "pending signature"
                    }}
                  </p>
                </div>
                <div
                  v-if="
                    document.totalSignatures &&
                      document.totalSignatures == document.completedSignatures
                  "
                  style="font-size: 0.775em; color: #5cb85c"
                >
                  All signed
                </div>
              </div>
              <b-tooltip
                :target="`document-${document.documentId}`"
                placement="bottom"
                triggers="hover"
              >
                <div
                  class="d-flex flex-column align-items-start text-align-left"
                >
                  <small>{{ document.form.name }}</small>
                  <small>{{ formatEocObj(document.episodeOfCare) }}</small>
                </div>
              </b-tooltip>
            </div>
          </div>
        </template>
      </div>
      <div
        class="main-container d-flex flex-column bg-white border-radius-top border-radius-bottom"
        :style="{
          width: isArray ? 'calc(100% - 600px)' : '100%',
          zIndex: 2
        }"
      >
        <div
          class="d-flex flex-row justify-content-between align-items-center bg-purple text-white border-radius-top"
          style="padding: 10px 20px;"
        >
          <div>
            <h5 class="modal-title font-weight-bold">{{ modalTitle }}</h5>
            <div class="modal-subtitle">{{ modalSubtitle }}</div>
          </div>
          <button
            type="button"
            aria-label="Close"
            class="close text-white"
            style="opacity: 1 !important"
            @click="closeModal"
          >
            ×
          </button>
        </div>
        <template v-if="currentDocument">
          <zoom-area ref="documentZoomArea" :hideControls="true" width="1025px">
            <vue-html2pdf
              :show-layout="false"
              :float-layout="false"
              :pdf-quality="2"
              :pdf-content-width="'100%'"
              :paginate-elements-by-height="1500"
              class="w-100"
            >
              <template slot="pdf-content">
                <div
                  v-if="singleHtml"
                  v-html-sanitize="singleHtml"
                  :id="currentDocument.documentId"
                  class="m-4"
                />
              </template>
            </vue-html2pdf>
          </zoom-area>
        </template>
        <div
          class="
            d-flex
            justify-content-between
            align-items-center
            w-100
            bg-extra-light-gray
            position-relative
            mt-auto
            border-top
            border-radius-bottom
          "
          style="padding: 10px 20px"
        >
          <div>
            <template v-if="singleHtml">
              <b-button
                v-if="
                  hasCurrentUserDocumentPermission(
                    currentDocument.form.id,
                    'hasEntryAccess'
                  )
                "
                class="mr-2"
                variant="primary"
                @click="showDocumentEditor = true"
              >
                Edit
              </b-button>
              <b-button class="mr-2" variant="primary" @click="printSingle">
                Print
              </b-button>
              <b-button
                v-if="currentDocument"
                class="mr-2"
                variant="primary"
                :disabled="downloadingPDF"
                @click="savePdfSingle"
                :style="{ cursor: downloadingPDF ? 'wait' : 'pointer' }"
              >
                Download
              </b-button>
              <b-button
                v-if="currentDocument"
                class="mr-2"
                variant="primary"
                @click="sendFax(false)"
              >
                Fax
              </b-button>
            </template>
          </div>
          <div style="font-size: 20px">
            <i
              @click="addDocumentZoomArea()"
              class="fa fa-search-plus zoom-control hover-text-primary"
            ></i>
            <i
              @click="lessDocumentZoomArea()"
              class="fa fa-search-minus zoom-control hover-text-primary"
            ></i>
          </div>
          <div style="width: 236px; text-align: right">
            <b-button @click="closeModal" variant="secondary"> Close </b-button>
          </div>
        </div>
      </div>
      <div class="sidebar">
        <div class="sidebar-inner">
          <div class="d-flex p-2 mb-3">
            <div
              class="
                sidebar-tab-title
                px-3
                text-primary
                border-bottom border-primary
              "
            >
              NOTES
            </div>
          </div>
          <document-notes-tab-content
            v-if="currentDocument"
            ref="documentNotes"
            :documentId="currentDocument.documentId"
            @onChange="refetchDocuments = true"
          />
        </div>
      </div>
    </div>
    <vue-html2pdf
      ref="html2Pdf"
      :show-layout="false"
      :float-layout="false"
      :enable-download="true"
      :pdf-quality="2"
      :pdf-content-width="'1025px'"
      :paginate-elements-by-height="1500"
      :html-to-pdf-options="pdfOptions"
      class="position-fixed w-100"
      style="top: 1000%"
      @hasDownloaded="hasDownloaded"
    >
      <template slot="pdf-content"> </template>
    </vue-html2pdf>
    <vue-html2pdf
      ref="html2PdfGroup"
      :show-layout="false"
      :float-layout="false"
      :enable-download="true"
      :pdf-quality="2"
      :pdf-content-width="'1025px'"
      :paginate-elements-by-height="1500"
      :manual-pagination="true"
      :html-to-pdf-options="pdfGroup"
      class="position-fixed w-100"
      style="top: 1000%"
      @hasDownloaded="hasDownloaded"
    >
      <template slot="pdf-content"> </template>
    </vue-html2pdf>
    <template v-if="currentEoc && currentDocument">
      <document-add-edit-modal
        v-if="showDocumentEditor"
        v-model="showDocumentEditor"
        :eocId="currentEoc.id"
        :clientId="currentEoc.patient.id"
        :formId="currentDocument.form.id"
        :submitId="currentDocument.documentId"
        :documentRefNumber="currentDocument.documentRefNumber"
        @onModify="onModify"
      />
    </template>
    <send-fax-modal
      v-if="showFaxModal"
      v-model="showFaxModal"
      :ids="this.ids"
      :sendFaxMethod="this.sendFaxMethod"
    ></send-fax-modal>
    <document-reorder
      v-if="documentReorderModal.show"
      v-model="documentReorderModal.show"
      :documents="documentReorderModal.documents"
      :actionLabel="documentReorderModal.actionLabel"
      @onModify="onModifyDocumentReorder"
      @onReorder="onReorder"
    />
  </b-modal>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import documentServer from "../../../server/document";
import scheduleEventServer from "../../../server/scheduleEvent";
import VueHtml2pdf from "vue-html2pdf";
import ZoomArea from "../../controls/ZoomArea";
import DocumentAddEditModal from "./DocumentAddEditModal.vue";
import DocumentNotesTabContent from "./DocumentNotesTabContent.vue";
import SendFaxModal from "../../fax/SendFaxModal.vue";
import DocumentReorder from "./DocumentReorder.vue";
import { formatDateOnly } from "../../../helpers/date";

export default {
  name: "DocumentViewModal",
  components: {
    VueHtml2pdf,
    ZoomArea,
    DocumentNotesTabContent,
    DocumentAddEditModal,
    SendFaxModal,
    DocumentReorder
  },
  props: {
    documentId: {
      type: [String, Array],
      required: true
    }
  },
  data() {
    return {
      ids: [],
      sendFaxMethod: "",
      documents: [],
      reorderedDocuments: [],
      currentDocument: null,
      singleUrl: null,
      singleHtml: null,
      singleBlobUrl: null,
      groupUrl: null,
      groupHtml: null,
      groupBlobUrl: null,
      downloadingPDF: false,
      showDocumentEditor: false,
      showFaxModal: false,
      refetchDocuments: false,
      documentReorderModal: {
        show: false,
        documents: null,
        callback: () => {},
        actionLabel: ""
      }
    };
  },
  async mounted() {
    this.documents = await documentServer.documentsFromIdsImproved(
      this.isArray ? this.documentId : [this.documentId]
    );

    if (this.documents.length) {
      this.setCurrentDocument(this.documents[0]);
    }

    if (this.documents.length > 1) {
      this.loadGroup();
    }
    this.reorderedDocuments = this.documents.map(document => ({
      value: document.documentId,
      label: `${document.documentRefNumber} ${
        document.form.name
      } (${this.formatEocObj(document.episodeOfCare)})`
    }));
  },
  methods: {
    ...mapActions("layout", ["show", "hide"]),
    async setCurrentDocument(doc) {
      this.currentDocument = doc;

      if (doc.mode == "File") {
        throw "Files cannot be opened from this viewer";
      }
      if (doc.type == "Group") {
        throw "Document Groups cannot be opened from this viewer";
      }

      if (doc.category == "groupSession") {
        this.singleUrl = scheduleEventServer.groupSessionDocumentViewUrl(
          doc.documentId
        );
      } else if (
        doc.category == "masterTreatmentPlan" &&
        !doc.parentDocumentId
      ) {
        this.singleUrl = documentServer.treatmentPlanDocumentViewUrl(
          doc.documentId
        );
      } else {
        this.singleUrl = documentServer.documentsViewUrl([doc.documentId]);
      }

      fetch(this.singleUrl.url, { headers: this.singleUrl.headers }).then(
        response => {
          const clone = response.clone();

          response.blob().then(blob => {
            this.singleBlobUrl = URL.createObjectURL(blob);
          });

          clone.text().then(html => {
            const parser = new DOMParser();
            const htmlDoc = parser.parseFromString(html, "text/html");
            this.singleHtml = htmlDoc.body.innerHTML.replace(/vh-100/gi, "");

            const content = this.$refs["html2Pdf"].$el.querySelector(
              ".content-wrapper"
            );
            const fragment = document
              .createRange()
              .createContextualFragment(this.singleHtml);

            content.appendChild(fragment);

            requestAnimationFrame(() => {
              Array.from(content.querySelectorAll(".document-single")).forEach(
                singleDoc => {
                  const rect = singleDoc.getBoundingClientRect();
                  singleDoc.style.transform = "scale(0.6925)";
                  singleDoc.style.transformOrigin = "0% 0% 0px";
                  singleDoc.style.height = `${rect.height * 0.6925}px`;
                }
              );
            });
          });
        }
      );
    },
    async loadGroup() {
      this.groupUrl = documentServer.documentsViewUrl(
        this.documents.map(d => d.documentId)
      );

      fetch(this.groupUrl.url, { headers: this.groupUrl.headers }).then(
        response => {
          const clone = response.clone();

          response.blob().then(blob => {
            this.groupBlobUrl = URL.createObjectURL(blob);
          });

          clone.text().then(html => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            this.groupHtml = doc.body.innerHTML.replace(/vh-100/gi, "");

            const content = this.$refs.html2PdfGroup.$el.querySelector(
              ".content-wrapper"
            );
            const fragment = document
              .createRange()
              .createContextualFragment(this.groupHtml);

            content.appendChild(fragment);

            requestAnimationFrame(() => {
              Array.from(content.querySelectorAll(".document-single")).forEach(
                singleDoc => {
                  const rect = singleDoc.getBoundingClientRect();
                  singleDoc.style.transform = "scale(0.6925)";
                  singleDoc.style.transformOrigin = "0% 0% 0px";
                  singleDoc.style.height = `${rect.height * 0.6925}px`;
                }
              );
            });
          });
        }
      );
    },
    sendFax(allDocuments = true) {
      this.sendFaxMethod = "sendDocumentsPdf";
      this.ids = this.reorderedDocuments.map(doc => doc.value);
      if (!allDocuments) {
        this.ids = [this.currentDocument.documentId];
      }
      if (!this.isArray) {
        if (
          this.currentDocument.category == "masterTreatmentPlan" &&
          !this.currentDocument.parentDocumentId
        ) {
          this.sendFaxMethod = "sendTreatmentPlanPdf";
          this.ids = [this.currentDocument.documentId];
        }
      }
      this.showFaxModal = true;
    },
    savePdfSingle() {
      let downloadMethod = documentServer.downloadDocumentPdf;
      let ids = [this.currentDocument.documentId];
      if (
        this.currentDocument.category == "masterTreatmentPlan" &&
        !this.currentDocument.parentDocumentId
      ) {
        downloadMethod = documentServer.downloadtreatmentPlanPdf;
        ids = this.currentDocument.documentId;
      }

      this.downloadingPDF = true;
      const filename = this.pdfSingle.filename;

      this.show();
      downloadMethod(ids)
        .then(res => {
          res.blob().then(blob => {
            let fileURL = URL.createObjectURL(blob);
            var link = document.createElement("a");
            link.href = fileURL;
            link.download = filename;
            link.dispatchEvent(new MouseEvent("click"));
          });
        })
        .finally(() => {
          this.downloadingPDF = false;
          this.hide();
        });
    },
    onReorder({ reorderedDocuments }) {
      this.reorderedDocuments = reorderedDocuments;
      this.documentReorderModal.callback();
    },
    onModifyDocumentReorder() {
      this.documentReorderModal = {
        show: false,
        documents: [],
        callback: () => {},
        actionLabel: ""
      };
    },
    showReorderModal(callback, actionLabel) {
      this.documentReorderModal = {
        show: true,
        documents: this.reorderedDocuments,
        callback,
        actionLabel
      };
    },
    savePdfGroup() {
      this.downloadingPDF = true;
      const filename = this.pdfGroup.filename;

      documentServer
        .downloadDocumentPdf(this.reorderedDocuments.map(doc => doc.value))
        .then(res => {
          res.blob().then(blob => {
            let fileURL = URL.createObjectURL(blob);
            var link = document.createElement("a");
            link.href = fileURL;
            link.download = filename;
            link.dispatchEvent(new MouseEvent("click"));
          });
        })
        .finally(() => (this.downloadingPDF = false));
    },
    printSingle() {
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.zIndex = -100;
      iframe.style.width = "2250px";
      iframe.src = this.singleBlobUrl;
      iframe.onload = function() {
        iframe.contentWindow.onafterprint = function() {
          document.body.removeChild(iframe);
        };

        setTimeout(() => {
          iframe.contentWindow.print();
        }, 500);
      };

      document.body.appendChild(iframe);
    },
    printGroup() {
      this.groupUrl = documentServer.documentsViewUrl(
        this.reorderedDocuments.map(d => d.value)
      );
      fetch(this.groupUrl.url, { headers: this.singleUrl.headers }).then(
        response => {
          response.blob().then(blob => {
            this.groupBlobUrl = URL.createObjectURL(blob);
            const iframe = document.createElement("iframe");
            iframe.style.position = "fixed";
            iframe.style.zIndex = -100;
            iframe.style.width = "2250px";
            iframe.src = this.groupBlobUrl;
            iframe.onload = function() {
              iframe.contentWindow.onafterprint = function() {
                document.body.removeChild(iframe);
              };

              setTimeout(() => {
                iframe.contentWindow.print();
              }, 500);
            };
            document.body.appendChild(iframe);
          });
        }
      );
    },
    hasDownloaded() {
      this.downloadingPDF = false;
    },
    addDocumentZoomArea() {
      this.$refs.documentZoomArea.addZoom();
    },
    lessDocumentZoomArea() {
      this.$refs.documentZoomArea.lessZoom();
    },
    closeModal() {
      this.$emit("onModify", { refetchDocuments: this.refetchDocuments });
      this.editorShown = false;
    },
    getStatusClass(document) {
      if (document.status == "Completed") return "success";
      if (document.status == "Pending") return "warning";
      if (document.status == "Draft") return "danger";
      return "text-danger bg-light";
    },
    getStatus(document) {
      return document ? document.status : "NOT CREATED";
    },
    onModify(e) {
      this.showDocumentEditor = false;
      this.editorShown = false;

      this.$emit("onModify", e);
    }
  },
  computed: {
    ...mapGetters("layout", ["hasCurrentUserDocumentPermission"]),
    currentEoc() {
      return this.currentDocument ? this.currentDocument.episodeOfCare : null;
    },
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    modalTitle() {
      if (this.currentDocument) {
        return `${
          this.currentDocument.documentRefNumber
            ? "Ref# " + this.currentDocument.documentRefNumber + " | "
            : ""
        }${this.currentDocument.form.name}`;
      }
      return "";
    },
    modalSubtitle() {
      const mrnDescription = this.currentEoc?.patient.mrn ? "Mr# " : "";
      return this.currentEoc
        ? mrnDescription +
            this.formatEoc(
              " | " + this.currentEoc.patient.firstName,
              this.currentEoc.patient.middleName,
              this.currentEoc.patient.preferredName,
              this.currentEoc.patient.lastName,
              this.currentEoc.patient.mrn,
              this.currentEoc.eocn
        ) +
        ` | ${formatDateOnly(this.currentEoc.patient.dateOfBirth)}` +
        ` | ${this.currentEoc.patient.pronoun
          ? this.currentEoc.patient.pronoun.name
          : ""
        }`
        : "";
    },
    modalSize() {
      return this.isArray ? "xl" : "lg";
    },
    pdfOptions() {
      return {
        image: { type: "jpeg", quality: 1 },
        margin: 12,
        html2canvas: { scale: 2.5 }
      };
    },
    pdfSingle() {
      const filename = `${this.modalTitle} ${
        this.modalSubtitle
      } - ${this.formatDateTimeSql(
        this.currentDocument.effectiveDate
          ? this.currentDocument.effectiveDate
          : this.currentDocument.createdAt
      )}.pdf`;

      return { ...this.pdfOptions, filename };
    },
    pdfGroup() {
      const filename = "Documents.pdf";
      return { ...this.pdfOptions, filename };
    },
    isArray() {
      return Array.isArray(this.documentId);
    }
  }
};
</script>

<style scoped>
.sidebar-menu-form-item {
  min-height: 54px;
}

.sidebar {
  width: 350px;
  background-color: #f5f8fc;
  display: flex;
  flex-direction: column;
  z-index: 1;
  border-top-right-radius: 0.3rem;
  border-bottom-right-radius: 0.3rem;
  margin-top: 10px;
  margin-bottom: 10px;
}
.sidebar-inner {
  width: 350px;
  height: 100%;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding: 15px;
}
.sidebar-tab-title {
  border-bottom-width: 3px !important;
  flex-grow: 1;
  font-size: 14px;
  padding-bottom: 10px;
  text-align: center;
}

.zoom-control {
  width: 37px;
}
.zoom-control:hover {
  font-size: 21px;
  cursor: pointer;
}
.border-radius-top {
  border-top-right-radius: 0.3rem;
  border-top-left-radius: 0.3rem;
}
.border-radius-bottom {
  border-bottom-right-radius: 0.3rem;
  border-bottom-left-radius: 0.3rem;
}
.main-container {
  -webkit-box-shadow: -10px 0px 13px -7px #000000a6,
    10px 0px 13px -7px #000000a6, 5px 5px 15px 5px rgba(0, 0, 0, 0);
  box-shadow: -10px 0px 13px -7px #000000a6, 10px 0px 13px -7px #000000a6,
    5px 5px 15px 5px rgba(0, 0, 0, 0);
}
</style>

<style>
.view-single-document-modal .modal-content {
  border: none;
  border-radius: 0 20px 20px 0;
}

.view-single-document-modal.multiple-documents .modal-content {
  border-radius: 20px;
}

.view-single-document-modal .modal-body {
  padding: 0px !important;
}

.content-wrapper .document-single:not(:last-child) {
  margin-bottom: 32px;
}

@media (min-width: 768px) {
  .content-wrapper .custom-col {
    flex-basis: 0 !important;
    flex-grow: 1 !important;
    max-width: 100% !important;
  }
}
.document-modal .modal-content {
  background-color: transparent;
}
</style>
